import React, { useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { CloseButton } from '@repo/common';
import TdButton from './Button';
import usePresence from './usePresence';
import useScrollLock from './useScrollLock';
import { cn } from './model/derive';

/**
 * 시나리오(브리프) — 전체 화면 모달.
 *   워크보드에 처음 들어올 때(리더는 T0, 팀원은 T0+1h 부터) 한 번 먼저 보여 주고,
 *   그 뒤에는 Resources 의 Scenario 카드에서 언제든 다시 연다. 열람 기록은 남기지 않는다.
 *
 * 읽기 전용 — 글을 고르거나 복사할 수 없다. 데이터는 Ops › Setup › Brief 의 Scenario · Deliverable.
 * 화면 전체를 덮어 다음 행동이 안 보이므로 아래에 늘 붙어 있는 버튼 줄을 둔다 —
 * 처음 볼 때는 Start(닫으면 워크보드 안내가 이어진다), 다시 열었을 때는 Back to workboard.
 */
/** 사용자 가이드 (A6 How it works) — 새 탭으로 연다 */
const USER_GUIDE_URL = '/2days/guide';
const block = (e) => e.preventDefault();

const ScenarioModal = ({ open, first = false, project, onClose }) => {
  const { mounted, shown } = usePresence(open);
  const closeRef = useRef(null);
  useScrollLock(mounted);

  useEffect(() => {
    if (!mounted) return undefined;
    const onKey = (e) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [mounted, onClose]);
  useEffect(() => {
    if (shown) closeRef.current?.querySelector('button')?.focus();
  }, [shown]);

  if (!mounted) return null;
  return ReactDOM.createPortal(
    <div
      role='dialog'
      aria-modal='true'
      aria-labelledby='scenario-title'
      // 복사 방지 — 선택 · 복사 · 잘라내기 · 우클릭 · 끌기를 막는다 (스크린샷은 막을 수 없다)
      onCopy={block}
      onCut={block}
      onContextMenu={block}
      onDragStart={block}
      className={cn(
        'fixed inset-0 z-[1200] flex select-none flex-col overflow-y-auto overscroll-contain bg-bg-twodays transition-[opacity,transform] duration-300 ease-out motion-reduce:transition-none',
        shown ? 'translate-y-0 opacity-100' : 'translate-y-3 opacity-0',
      )}
    >
      <div className='sticky top-0 flex items-center justify-between gap-3 border-b border-green-deep03/[0.08] bg-[color-mix(in_srgb,var(--bg-twodays)_95%,transparent)] px-4 py-3 backdrop-blur sm:px-8'>
        <span className='flex items-center gap-2 text-[14px] text-text-secondary'>
          <span className='flex h-7 w-7 items-center justify-center rounded-lg bg-green-deep02 text-[12px] font-medium text-grayscale-gray00'>
            2d
          </span>
          <span className='truncate'>
            {/* 프로젝트 이름 앞의 "2days · " 는 겹치니 뺀다 */}2 days project ·{' '}
            {project.title?.replace(/^2\s?days\s*·\s*/i, '')}
            {project.teamName && ` · ${project.teamName}`}
          </span>
        </span>
        <span ref={closeRef} className='flex'>
          <CloseButton onClick={onClose} />
        </span>
      </div>

      <div className='mx-auto flex w-full max-w-[720px] flex-1 flex-col gap-8 px-4 py-10 sm:px-8 sm:py-14'>
        <div className='flex flex-col gap-3'>
          <h1
            id='scenario-title'
            className='text-[28px] font-medium leading-[125%] tracking-[-0.01em] text-text-primary sm:text-[34px]'
          >
            Scenario
          </h1>
        </div>

        <section className='flex flex-col gap-3 rounded-2xl border border-green-deep03/[0.08] bg-grayscale-gray00 p-6 shadow-4 sm:p-8'>
          <h2 className='text-[13px] font-medium text-text-secondary'>The situation</h2>
          <p className='whitespace-pre-line text-[16px] leading-[170%] text-text-primary'>
            {project.scenario || 'The scenario isn’t ready yet.'}
          </p>
        </section>

        {project.deliverable && (
          <section className='flex flex-col gap-3 rounded-2xl border border-green-deep03/[0.08] bg-grayscale-gray00 p-6 shadow-4 sm:p-8'>
            <h2 className='text-[13px] font-medium text-text-secondary'>What to build</h2>
            <p className='whitespace-pre-line text-[16px] leading-[170%] text-text-primary'>
              {project.deliverable}
            </p>
          </section>
        )}

        <p className='text-[14px] leading-[150%] text-text-secondary'>
          To get the most out of the 2days Project, check out our{' '}
          <a
            href={USER_GUIDE_URL}
            target='_blank'
            rel='noopener noreferrer'
            className='text-green-deep03 underline underline-offset-2'
          >
            user guide
          </a>
          . You can also contact us anytime at{' '}
          <a
            href='mailto:contact@ko-ready.com'
            className='text-green-deep03 underline underline-offset-2'
          >
            contact@ko-ready.com
          </a>
          .
        </p>
      </div>

      {/* 아래에 늘 붙어 있는 다음 행동 */}
      <div className='sticky bottom-0 border-t border-green-deep03/[0.08] bg-[color-mix(in_srgb,var(--bg-twodays)_95%,transparent)] backdrop-blur'>
        <div className='mx-auto flex w-full max-w-[720px] items-center justify-between gap-4 px-4 py-3 sm:px-8 sm:py-4'>
          <span className='hidden text-[14px] leading-[150%] text-text-secondary sm:block'>
            {first
              ? 'Next, a quick tour of your workboard.'
              : 'You can reopen this anytime from Resources.'}
          </span>
          <TdButton size='l' onClick={onClose} className='w-full px-8 sm:w-auto'>
            {first ? 'Start' : 'Back to workboard'}
          </TdButton>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default ScenarioModal;
